import { hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { PopupData, Service } from 'webapp-types';
import { BillingFeature } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import BillingDowngradeRowComponent from 'webapp/components/billing/downgrade-row';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

// Used ONLY for the new 2026sept Agency plan when we go over clients / sources count
// because this has no "downgrade" to a different plan option
// we land here pretty much only after client packs have been reduced but clients/sources not removed
export default class BillingPopupAgencyDowngradeComponent extends PopupInnerModalComponent<PopupData.None> {
	@service('account') declare accountService: Service.Account;
	@service('billing') declare billingService: Service.Billing;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('model-manager/spaces') declare mmSpacesService: Service.ModelManager.Spaces;

	@tracked showSources = false;
	@tracked showClients = false;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.showSources = !!this.sourcesLimit;
		this.showClients = !!this.clientsLimit;

		this.analyticsService.event(TrackingEvent.AGENCY_DOWNGRADE_POPUP_SHOWN);
	}

	get agencyDowngradeLimitsBreached() {
		return this.billingService.agencyClientPackDowngradeLimitsBreached;
	}

	get sourcesLimit() {
		return this.agencyDowngradeLimitsBreached?.[BillingFeature.SOURCES];
	}

	get clientsLimit() {
		return this.agencyDowngradeLimitsBreached?.[BillingFeature.CLIENTS];
	}

	get sources() {
		return this.mmSourcesService.sourceModels.filter((source) => {
			return !source.isRoot && !source.isDataset;
		});
	}

	get clients() {
		return this.mmSpacesService.otherSpaceModels;
	}

	get isDone() {
		return !this.agencyDowngradeLimitsBreached;
	}

	@action
	purchasePacks() {
		this.billingService.openClientPacksPurchasePopup();
		this.close();
	}

	<template>
		<div class="content">
			<CommonUiIconComponent @name="DragAndDropArea" @size="xl" />
			<h2>Reduce your usage</h2>

			{{#if this.showSources}}
				<div class="section">
					<p>
						{{#if this.sourcesLimit}}
							You have
							<strong>{{this.sourcesLimit.current}} data sources</strong>, but your
							plan includes
							<strong>{{this.sourcesLimit.max}}</strong>.
							<br />
							Reduce the number of data sources
							<a {{on "click" this.purchasePacks}}>
								or purchase additional account packs
							</a>
							to continue using the app.
						{{else}}
							Data sources are now under limits.
						{{/if}}
					</p>
					<ul>
						{{#each this.sources as |source|}}
							<BillingDowngradeRowComponent @model={{source}} @type="source" />
						{{/each}}
					</ul>
				</div>
			{{/if}}

			{{#if this.showClients}}
				<div class="section">
					<p>
						{{#if this.clientsLimit}}
							You have
							<strong>{{this.clientsLimit.current}} accounts</strong>, but your plan
							includes
							<strong>{{this.clientsLimit.max}}</strong>.
							<br />
							Remove excess accounts
							<a {{on "click" this.purchasePacks}}>
								or purchase additional account packs
							</a>
							to continue using the app.
						{{else}}
							The number of accounts is within the allowed limit.
						{{/if}}
					</p>
					<ul>
						{{#each this.clients as |client|}}
							<BillingDowngradeRowComponent
								@model={{client}}
								@agencyLimitations={{hash mustDeleteClients=this.clientsLimit}}
								@type="client"
							/>
						{{/each}}
					</ul>
				</div>
			{{/if}}

			<div class="controls2 controls2--center">
				<SubmitButtonComponent
					@text="Done"
					@size="big"
					@noDelay={{true}}
					@isDisabled={{this.isDone}}
					@onTrigger={{this.close}}
				/>
			</div>

		</div>
	</template>
}
